"use client";

import Chevron from "@/app/assets/images/svg/chevron.svg";
import { useScopedI18n } from "@/i18n/client";
import clsx from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FC, useMemo, useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import styles from "./Pagination.module.scss";

interface Props {
	initialPage?: number;
	page?: number;
	pageCount?: number;
	onPageChange?: (page: number) => void;
	className?: string;
	routing?: "window" | "next";
	prefetchCount?: number; // Number of pages to prefetch on either side of the current page
}

const Pagination: FC<Props> = ({
	initialPage,
	page = 0,
	pageCount = 0,
	onPageChange,
	className = "",
	routing = "window",
	prefetchCount = 1, // Default to prefetching 1 page in each direction
}) => {
	const scopedT = useScopedI18n("text");
	const pathname = usePathname();
	const params = useSearchParams();
	const router = useRouter();

	// Track current page for prefetching
	const [currentPage, setCurrentPage] = useState<number | undefined>(undefined);
	// Track previously prefetched pages to avoid duplicate requests
	const prefetchedPages = useRef<Set<number>>(new Set());

	const getPageUrl = (pageNum: number): string => {
		const updatedParams = new URLSearchParams(params.toString());

		// don't include page param on first page
		if (pageNum === 0) {
			updatedParams.delete("page");
		} else {
			updatedParams.set("page", `${pageNum + 1}`);
		}

		const paramsString = updatedParams.toString();
		return `${pathname}${paramsString ? `?${paramsString}` : ""}`;
	};

	// Function to prefetch a specific page
	const prefetchPage = async (pageNum: number) => {
		// Skip invalid pages or already prefetched pages
		if (
			pageNum < 0 ||
			(pageCount && pageNum >= pageCount) ||
			prefetchedPages.current.has(pageNum)
		) {
			return;
		}

		try {
			// Mark as prefetched to prevent duplicate requests
			prefetchedPages.current.add(pageNum);

			const url = getPageUrl(pageNum);

			// Perform the prefetch
			const response = await fetch(url, {
				method: "GET",
				headers: {
					"x-nextjs-prefetch": "1",
				},
				cache: "force-cache",
			});

			if (!response.ok) {
				console.warn(`Failed to prefetch page ${pageNum + 1}`);
				// Remove from prefetched set if fetch failed
				prefetchedPages.current.delete(pageNum);
			}
		} catch (error) {
			console.warn(`Error prefetching page ${pageNum + 1}:`, error);
			prefetchedPages.current.delete(pageNum);
		}
	};

	const updatePageParam = (page: number) => {
		const href = getPageUrl(page);

		if (routing === "window") {
			return window.history.replaceState("", "", href);
		}

		if (routing === "next") {
			return router.push(href);
		}
	};

	const initialPageMemo = useMemo(() => {
		if (!pageCount) {
			return undefined;
		}

		const pageParam = params.get("page");
		const page = initialPage ? initialPage : Number(pageParam || 1);

		// check for page is greater than total pages
		let validPage = page > pageCount ? pageCount : page;

		//check to if page is less than 1 (the lowest)
		validPage = validPage < 1 ? 1 : validPage;

		validPage = validPage - 1;

		// update page param is it doesn't match
		if (validPage !== page - 1) {
			updatePageParam(validPage);
		}

		// Set the current page for prefetching
		setCurrentPage(validPage);
		// Add initial page to prefetched set
		prefetchedPages.current.add(validPage);

		return validPage;
	}, [pageCount]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		updatePageParam(page);
		onPageChange?.(page);
	};

	// Prefetch adjacent pages when current page changes
	useEffect(() => {
		if (currentPage === undefined || !pageCount) return;

		// Prefetch next pages sequentially
		for (let i = 1; i <= prefetchCount; i++) {
			const pageToFetch = currentPage + i;
			if (pageToFetch < pageCount) {
				prefetchPage(pageToFetch);
			}
		}

		// Prefetch previous pages sequentially
		for (let i = 1; i <= prefetchCount; i++) {
			const pageToFetch = currentPage - i;
			if (pageToFetch >= 0) {
				prefetchPage(pageToFetch);
			}
		}
	}, [currentPage, pageCount, prefetchCount]);

	if (!pageCount) {
		return null;
	}

	return (
		<nav className={clsx([className, styles.outer])} aria-label='pagination'>
			<ReactPaginate
				initialPage={initialPageMemo}
				previousLabel={<Chevron aria-labelledby='directional chevron' />}
				nextLabel={<Chevron aria-labelledby='directional chevron' />}
				pageCount={pageCount}
				onPageChange={({ selected }) => handlePageChange(selected)}
				containerClassName={styles.container}
				previousClassName={styles.previous}
				pageLinkClassName={styles.item}
				nextClassName={styles.next}
				disabledClassName={styles.disabled}
				activeClassName={styles.active}
				breakLabel={"..."}
				breakClassName={styles.break}
				pageRangeDisplayed={5}
				marginPagesDisplayed={1}
			/>
		</nav>
	);
};

export default Pagination;
